/**
 * pickerState — what the two-panel picker shows, decided without React.
 *
 * THE PICKER RUNS ITS OWN FILTER RATHER THAN cmdk's, and this module is that filter. Two things
 * the direction asks for are impossible while cmdk owns filtering: the selected row has to stay
 * at the top of the list where it can be seen and unpicked, and the `+ Create "<typed>"` row has
 * to be the LAST ROW ALWAYS — including when the typed text matches nothing, which is the moment
 * it matters most. `shouldFilter={false}` hands both back, and the cost is this file.
 *
 * The matching rule is EVERY TERM SOMEWHERE, not the whole string in one piece: an admin who
 * knows a witness as "Ama" and remembers the last four digits types `ama 0980`, and a substring
 * search over the concatenated row finds nothing. Terms are matched against whatever the caller
 * put in `search` — name, phone, plate, policy number — so the row decides what it answers to.
 *
 * PURE, AND REACT-FREE — vitest runs node-env over `.ts` only.
 */

/** One row of a picker, whatever the picker is picking. */
export interface PickerRow<T> {
    /** Stable identity: the selected row is found by this, and it never reaches the eye. */
    id: string;
    /** Everything this row can be found by, in one string. Matched case-insensitively. */
    search: string;
    item: T;
}

/** Trim, fold case, and split on whitespace. An empty query yields no terms. */
export function queryTerms(query: string): string[] {
    return query.trim().toLowerCase().split(/\s+/).filter(Boolean);
}

/** True when every term the admin typed appears somewhere in the row's searchable text. */
export function matchesQuery(search: string, query: string): boolean {
    const terms = queryTerms(query);
    if (terms.length === 0) return true;
    const haystack = search.toLowerCase();
    return terms.every((term) => haystack.includes(term));
}

/**
 * The rows to draw, in order.
 *
 * THE SELECTED ROW IS PINNED FIRST WHEN IT SURVIVES THE FILTER — and only then. Pinning it
 * unconditionally, which is what the sheet this replaces did, leaves the person you are replacing
 * sitting above the person you are typing the name of.
 */
export function filterPickerRows<T>(
    rows: PickerRow<T>[],
    query: string,
    selectedId?: string | null,
): PickerRow<T>[] {
    const matched = rows.filter((row) => matchesQuery(row.search, query));
    if (!selectedId) return matched;

    const selected = matched.find((row) => row.id === selectedId);
    if (!selected) return matched;
    return [selected, ...matched.filter((row) => row.id !== selectedId)];
}

/**
 * The last row's label.
 *
 * IT NAMES WHAT WILL BE CREATED, VERBATIM. `Create "Ama Serwaa"` is a different promise from
 * `Create a new witness`, and the difference is exactly whether the panel will open seeded.
 */
export function createRowLabel(query: string, noun: string): string {
    const typed = query.trim();
    return typed ? `Create “${typed}”` : `Create a new ${noun}`;
}

/**
 * What the list says when nothing matched.
 *
 * IT POINTS AT THE ROW UNDERNEATH rather than dead-ending — the create row is always there, and
 * an empty state that does not say so reads as "this person cannot be added here".
 */
export function emptyListText(query: string, noun: string): string {
    return query.trim()
        ? `No ${noun} by that name. Create one below.`
        : `No ${noun} on file yet. Create one below.`;
}
